import {
    faBan,
    faPersonDressSimple,
    faPersonSimple,
    faSteeringWheel,
} from '@fortawesome/pro-solid-svg-icons';
import {
    GetSeatsApiApiV2GetSeatsGet,
    type ServiceInfoDto,
} from '@clients/BusApi';
import type { Seat } from '@itinerary/trip/bus/types';
import { SeatStatus } from '@itinerary/trip/bus/types';
import { Gender } from '@store/form';
import { PaxType } from '@utils/types';
import type Bus from '@itinerary/trip/bus/Bus';
import PassengerForm from '@itinerary/PassengerForm';
import { ReserveType } from '@utils/itinerary';

export interface BusSummary {
    status: SeatStatus;
    numbers: Record<number, number>;
}

const numbers = ['اول', 'دوم', 'سوم', 'چهارم'];

class BusSelectedSeat {
    status: SeatStatus = SeatStatus.Empty;
    selectedNumbers: number[] = [];
    isRoute = false;

    private get routeSubtitle() {
        const result: string[] = [];
        for (let num = 0; num < this.selectedNumbers.length; num++) {
            if (this.selectedNumbers[num]) {
                result.push(
                    `صندلی ${this.selectedNumbers[num]} اتوبوس ${numbers[num]}`
                );
            }
        }
        return result.join(' و ');
    }

    private get normalSubtitle() {
        if (this.selectedNumbers.length > 1) {
            const resultSubTitles: string[] = [];
            if (this.selectedNumbers[0]) {
                resultSubTitles.push(`صندلی ${this.selectedNumbers[0]} رفت`);
            }
            if (this.selectedNumbers[1]) {
                resultSubTitles.push(`صندلی ${this.selectedNumbers[1]} برگشت`);
            }
            return resultSubTitles.join(' و ');
        } else {
            if (this.selectedNumbers[0]) {
                return `صندلی ${this.selectedNumbers[0]}`;
            }
            return '';
        }
    }

    get subtitle() {
        if (this.isRoute) {
            return this.routeSubtitle;
        } else {
            return this.normalSubtitle;
        }
    }
}
const seatsInfo = ref<ServiceInfoDto[]>([]);
const isSeatsLoading = ref<boolean[]>([]);
const selectedSeats = ref<BusSelectedSeat[]>([]);
const seatsAbortController = ref<AbortController>();

class SeatImpl implements Seat {
    constructor(
        private seat: Seat,
        private busIndex: number
    ) {}

    private getIcon(status: SeatStatus) {
        if (status === SeatStatus.Wheel) {
            return faSteeringWheel;
        }
        if (status === SeatStatus.Male) {
            return faPersonSimple;
        }
        if (status === SeatStatus.Female) {
            return faPersonDressSimple;
        }
        return faBan;
    }

    get icon() {
        return this.getIcon(this.status);
    }

    get number() {
        return this.seat.number || -1;
    }

    get status() {
        return (
            selectedSeats.value.find(
                s => s.selectedNumbers[this.busIndex] === this.number
            )?.status ?? this.seat.status
        );
    }

    get selected() {
        return selectedSeats.value.some(
            s => s.selectedNumbers[this.busIndex] === this.number
        );
    }
}

export function useBusSeatHandler() {
    const formStore = useFormStore();
    const autoReserveStore = useAutoReserveStore();
    const invoiceStore = useInvoiceStore();
    const { paxForms } = classToRefs(formStore);

    function terminate() {
        seatsAbortController.value?.abort();
        seatsAbortController.value = undefined;
    }

    function reset() {
        terminate();
        seatsInfo.value = [];
        isSeatsLoading.value = [];
        selectedSeats.value = [];
    }

    const buses = computed<Bus[]>(() =>
        formStore.itineraryPackages
            .filter(pkg => pkg.isTripPackage())
            .filter(pkg => pkg.hasSeatSelection)
            .map(pkg => pkg.segments.map(segment => segment.trips))
            .flat(2)
            .filter(trip => trip.reserveType === ReserveType.Bus)
            .map(trip => trip as Bus)
    );

    const isRoute = computed(() =>
        formStore.itineraryPackages.some(pkg => pkg.isRoutePackage())
    );

    const hasSeatSelection = computed(
        () => !!buses.value.length && !autoReserveStore.isAutoReserve
    );

    async function loadSeats() {
        // Cancel any pending requests before starting new ones...
        terminate();
        seatsAbortController.value = new AbortController();
        const { signal } = seatsAbortController.value;

        const loadActions = buses.value.map((bus, index) => async () => {
            isSeatsLoading.value[index] = true;
            const { data } = await GetSeatsApiApiV2GetSeatsGet(
                {
                    busId: bus.id,
                    convertFemaleToMale: false,
                },
                { signal }
            );
            seatsInfo.value[index] = data;
            isSeatsLoading.value[index] = false;
        });
        await Promise.all(loadActions.map(p => p()));
    }

    const isValid = computed(() => {
        if (!hasSeatSelection.value) {
            return true;
        }
        if (
            selectedSeats.value.some(selectedSeat =>
                selectedSeat.selectedNumbers.some(num => !num)
            )
        ) {
            return false;
        }
        return (
            selectedSeats.value.flatMap(
                ({ selectedNumbers }) => selectedNumbers
            ).length >=
            minCount.value * buses.value.length
        );
    });

    const passCount = computed(() =>
        Math.max(minCount.value, selectedSeats.value.length)
    );

    const h2Names = computed(() =>
        buses.value.map(bus => {
            const originName = bus.originCityName || '';
            const destinationName = bus.destinationCityName || '';
            return originName && destinationName
                ? `${originName} به ${destinationName}`
                : '';
        })
    );

    const minCount = computed(
        () => formStore.adultCount + formStore.childCount
    );

    const canAddSeat = computed(() =>
        formStore.itineraryPackages.every(
            pkg => pkg.isBusPackage() && !pkg.isRoutePackage()
        )
    );

    const seatMapping = computed<Seat[][][]>(() =>
        seatsInfo.value.map(
            (info, busIndex) =>
                info.seats?.map(seatRow =>
                    seatRow.map((seat: Seat) => new SeatImpl(seat, busIndex))
                ) || []
        )
    );

    function updateGender(seatIndex: number, gender: Gender) {
        if (!hasSeatSelection.value || gender === Gender.None) {
            return;
        }
        const selectedSeat = selectedSeats.value[seatIndex];
        if (!selectedSeat) {
            return;
        }
        selectedSeat.status =
            gender === Gender.Male ? SeatStatus.Male : SeatStatus.Female;
    }

    function removeSeat(seatIndex: number) {
        if (!hasSeatSelection.value) {
            return;
        }
        const selectedSeat = selectedSeats.value[seatIndex];
        if (!selectedSeat) {
            return;
        }
        selectedSeats.value = selectedSeats.value.filter(
            seat => seat !== selectedSeat
        );
    }

    //#region toggleSeat: all codes below are meant to be used inside toggleSeat function and not independently.
    function toggleSeat(
        busIndex: number,
        seatNumber: number
    ): { type: 'add' } | { type: 'remove'; index: number } | undefined {
        if (seatNumber < 0) {
            return;
        }
        const status = getSeatStatus(busIndex, seatNumber);
        switch (status) {
            case SeatStatus.Empty:
                return handleEmptySeatToggle(busIndex, seatNumber);
            case SeatStatus.Male:
                handleMaleSeatToggle(busIndex, seatNumber);
                break;
            case SeatStatus.Female:
                return handleFemaleSeatToggle(busIndex, seatNumber);
        }
    }

    function getSeatStatus(busIndex: number, seatNumber: number) {
        const seat = selectedSeats.value.find(
            seat => seat.selectedNumbers[busIndex] === seatNumber
        );
        if (seat) {
            return seat.status;
        }
        return (seatsInfo.value[busIndex].seats?.flat() ?? []).find(
            seat => seat.number === seatNumber
        )?.status;
    }

    const canAddNewSeat = computed(
        () => selectedSeats.value.length !== minCount.value || canAddSeat.value
    );

    function handleEmptySeatToggle(busIndex: number, seatNumber: number) {
        // find the first orphan seat
        const orphanSeatIndex = findOrphanSeatIndex(busIndex);
        if (orphanSeatIndex !== -1) {
            updateOrphanSeat(busIndex, seatNumber, orphanSeatIndex);
        } else if (canAddNewSeat.value) {
            return addNewSeat(busIndex, seatNumber);
        }
    }

    function findOrphanSeatIndex(busIndex: number) {
        return selectedSeats.value.findIndex(
            seat => !seat.selectedNumbers[busIndex]
        );
    }

    function updateOrphanSeat(
        busIndex: number,
        seatNumber: number,
        orphanSeatIndex: number
    ) {
        const orphanSeat = selectedSeats.value[orphanSeatIndex];
        orphanSeat.selectedNumbers[busIndex] = seatNumber;
        orphanSeat.status = SeatStatus.Male;
        const paxForm = paxForms.value[orphanSeatIndex];
        const isMetaObject = typeof paxForm.metadata === 'object';
        paxForm.metadata = {
            ...(isMetaObject ? paxForm.metadata : {}),
            gender: Gender.Male,
        };
        paxForm.subTitle = orphanSeat.subtitle;
    }

    function addNewSeat(busIndex: number, seatNumber: number) {
        const busSelectedSeat = new BusSelectedSeat();
        busSelectedSeat.isRoute = isRoute.value;
        busSelectedSeat.selectedNumbers[busIndex] = seatNumber;
        busSelectedSeat.status = SeatStatus.Male;
        selectedSeats.value.push(busSelectedSeat);
        if (selectedSeats.value.length > paxForms.value.length) {
            return addNewPaxForm(busSelectedSeat, busIndex);
        } else {
            const paxForm = paxForms.value[selectedSeats.value.length - 1];
            const isMetaObject = typeof paxForm.metadata === 'object';
            paxForm.metadata = {
                ...(isMetaObject ? paxForm.metadata : {}),
                gender: Gender.Male,
            };
            paxForm.subTitle = busSelectedSeat.subtitle;
        }
    }

    function addNewPaxForm(busSelectedSeat: BusSelectedSeat, busIndex: number) {
        const newForm = new PassengerForm({
            ...paxForms.value[0],
            subTitle: busSelectedSeat.subtitle,
        });
        const isMetaObject = typeof newForm.metadata === 'object';
        newForm.metadata = {
            ...(isMetaObject ? newForm.metadata : {}),
            gender: Gender.Male,
        };
        paxForms.value.push(newForm);
        formStore.adultCount++;
        updatePriceEntries(busIndex);
        return { type: 'add' } as const;
    }

    function handleMaleSeatToggle(busIndex: number, seatNumber: number) {
        const seatIndex = selectedSeats.value.findIndex(
            seat => seat.selectedNumbers[busIndex] === seatNumber
        );
        selectedSeats.value[seatIndex].status = SeatStatus.Female;
        paxForms.value[seatIndex].metadata!.gender = Gender.Female;
    }

    function handleFemaleSeatToggle(busIndex: number, seatNumber: number) {
        const seatIndex = selectedSeats.value.findIndex(
            seat => seat.selectedNumbers[busIndex] === seatNumber
        );
        const selectedSeat = selectedSeats.value[seatIndex];
        if (!selectedSeat) {
            return;
        }
        if (!selectedSeat.selectedNumbers[busIndex]) {
            selectedSeats.value = selectedSeats.value.filter(
                seat => seat !== selectedSeat
            );
            if (paxForms.value.length > 1) {
                paxForms.value.splice(seatIndex, 1);
                formStore.adultCount--;
                updatePriceEntries(busIndex);
                return { type: 'remove', index: seatIndex } as const;
            }
        } else {
            selectedSeat.selectedNumbers[busIndex] = 0;
        }
    }

    function updatePriceEntries(busIndex: number) {
        invoiceStore.changeEntryPriceCountByPaxType({
            entryIndex: busIndex,
            paxType: PaxType.Seat,
            count: selectedSeats.value.length,
        });
    }
    //#endregion

    const summary = computed<BusSummary[]>(() =>
        selectedSeats.value.map(seat => ({
            status: seat.status as unknown as SeatStatus,
            numbers: buses.value.reduce(
                (a, bus, busIndex) => ({
                    ...a,
                    [bus.id]: seat.selectedNumbers[busIndex],
                }),
                {} as Record<number, number>
            ),
        }))
    );

    return {
        isValid,
        passCount,
        h2Names,
        isSeatsLoading,
        seatMapping,
        seatsInfo,
        hasSeatSelection,
        buses,
        selectedSeats,
        summary,
        loadSeats,
        updateGender,
        removeSeat,
        toggleSeat,
        reset,
        terminate,
    };
}
